/*
 * @Author: Ryan
 * @Date: 2026-01-09
 * @Description: 视频翻译演示区块
 */
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useContext, useRef, useState } from 'react';

import StartButton from '@/3.0/ai-avatar-generator/seo/StartButton';
import { RightContext } from '@/components/RightProvider/context';
import { VIDEO_TRANSLATOR_URL } from '@/landing/video-translator/config';

import styles from './index.module.scss';

// 播放按钮 SVG
const PlayIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="64"
    height="64"
    viewBox="0 0 64 64"
    fill="none"
  >
    <rect
      x="0.5"
      y="0.5"
      width="63"
      height="63"
      rx="31.5"
      fill="white"
      fillOpacity="0.1"
    />
    <rect x="0.5" y="0.5" width="63" height="63" rx="31.5" stroke="white" />
    <path
      d="M41.5038 30.3359C42.6913 31.1275 42.6913 32.8725 41.5038 33.6641L29.1094 41.9271C27.7803 42.8131 26 41.8603 26 40.2629L26 23.7371C26 22.1397 27.7803 21.1869 29.1094 22.0729L41.5038 30.3359Z"
      fill="white"
    />
  </svg>
);

// 声音开启图标
const SoundOnIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
  >
    <path
      d="M10.8 2.69971V18.8997C7.65005 18.8997 5.30937 14.7773 5.30937 14.7773H2.70005C2.20299 14.7773 1.80005 14.3744 1.80005 13.8773V7.65457C1.80005 7.1575 2.20299 6.75457 2.70005 6.75457H5.30937C5.30937 6.75457 7.65005 2.69971 10.8 2.69971Z"
      stroke="#333333"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M14.4 6.75C14.6805 7.00043 14.9347 7.28087 15.1579 7.58646C15.8124 8.48268 16.2 9.59504 16.2 10.8C16.2 11.9945 15.8191 13.098 15.1748 13.9902C14.9476 14.3048 14.6876 14.5932 14.4 14.85"
      stroke="#333333"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// 声音关闭图标
const SoundOffIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
  >
    <path
      d="M10.8 2.69971V18.8997C7.65005 18.8997 5.30937 14.7773 5.30937 14.7773H2.70005C2.20299 14.7773 1.80005 14.3744 1.80005 13.8773V7.65457C1.80005 7.1575 2.20299 6.75457 2.70005 6.75457H5.30937C5.30937 6.75457 7.65005 2.69971 10.8 2.69971Z"
      stroke="#333333"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M14 8L18 14M18 8L14 14"
      stroke="#333333"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

// 视频演示数据
const DEMO_VIDEOS = [
  {
    id: 'english',
    language: 'English',
    flag: '/images/country/flag-us.png',
    videoUrl:
      'https://assets.falcocut.ai/tools/video-translator/break-language-barriers-with-ai-video-translation-en.mp4',
    poster:
      'https://assets.falcocut.ai/tools/video-translator/break-language-barriers-with-ai-video-translation-en-poster.webp',
  },
  {
    id: 'spanish',
    language: 'Spanish',
    flag: '/images/country/flag-es.png',
    videoUrl:
      'https://assets.falcocut.ai/tools/video-translator/break-language-barriers-with-ai-video-translation-es.mp4',
    poster:
      'https://assets.falcocut.ai/tools/video-translator/break-language-barriers-with-ai-video-translation-es-poster.webp',
  },
  {
    id: 'japanese',
    language: 'Japanese',
    flag: '/images/country/flag-jp.png',
    videoUrl:
      'https://assets.falcocut.ai/tools/video-translator/break-language-barriers-with-ai-video-translation-ja.mp4',
    poster:
      'https://assets.falcocut.ai/tools/video-translator/break-language-barriers-with-ai-video-translation-ja-poster.webp',
  },
];

interface VideoTranslationDemosProps {
  title: string;
  subtitle: string;
  ctaText: string;
}

const VideoTranslationDemos: React.FC<VideoTranslationDemosProps> = ({
  title,
  subtitle,
  ctaText,
}) => {
  const router = useRouter();
  const rightContext = useContext(RightContext);
  const videoRefs = useRef<{ [key: string]: HTMLVideoElement | null }>({});

  const [playingId, setPlayingId] = useState<string | null>(null);
  const [mutedStates, setMutedStates] = useState<{ [key: string]: boolean }>({
    english: true,
    spanish: true,
    japanese: true,
  });

  // 播放视频
  const handlePlay = (id: string) => {
    // 暂停其他视频
    Object.entries(videoRefs.current).forEach(([key, video]) => {
      if (key !== id && video) {
        video.pause();
      }
    });

    const video = videoRefs.current[id];
    if (video) {
      if (playingId === id) {
        video.pause();
        setPlayingId(null);
      } else {
        video.play();
        setPlayingId(id);
      }
    }
  };

  // 切换静音
  const toggleMute = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const video = videoRefs.current[id];
    if (video) {
      video.muted = !video.muted;
      setMutedStates((prev) => ({
        ...prev,
        [id]: video.muted,
      }));
    }
  };

  // CTA 点击
  const handleCtaClick = async () => {
    if (!rightContext.getIsLoggedIn()) {
      await rightContext.getUserLoginState({
        onReady: () => {
          router.push(VIDEO_TRANSLATOR_URL);
        },
      });
      return;
    }
    router.push(VIDEO_TRANSLATOR_URL);
  };

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        {/* 标题区域 */}
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>

        {/* 视频卡片区域 */}
        <div className={styles.videosWrapper}>
          <div className={styles.videosContainer}>
            {DEMO_VIDEOS.map((demo) => (
              <div key={demo.id} className={styles.videoCard}>
                <div className={styles.videoWrapper} onClick={() => handlePlay(demo.id)}>
                  {/* 视频占位或实际视频 */}
                  {demo.videoUrl ? (
                    <video
                      ref={(el) => {
                        videoRefs.current[demo.id] = el;
                      }}
                      src={demo.videoUrl}
                      poster={demo.poster}
                      muted={mutedStates[demo.id]}
                      loop
                      playsInline
                      preload="none"
                      className={styles.video}
                      onEnded={() => setPlayingId(null)}
                    />
                  ) : (
                    <div className={styles.videoPlaceholder}>
                      <span>Video Coming Soon</span>
                    </div>
                  )}

                  {/* 播放按钮 */}
                  {playingId !== demo.id && (
                    <div className={styles.playButton}>
                      <PlayIcon />
                    </div>
                  )}

                  {/* 语言标签 */}
                  <div className={styles.languageTag}>
                    <Image src={demo.flag} alt={demo.language} width={20} height={14} />
                    <span>{demo.language}</span>
                    <button
                      type="button"
                      className={styles.soundButton}
                      onClick={(e) => toggleMute(demo.id, e)}
                      aria-label={mutedStates[demo.id] ? 'Unmute' : 'Mute'}
                    >
                      {mutedStates[demo.id] ? <SoundOffIcon /> : <SoundOnIcon />}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA 按钮 */}
        <div className={styles.ctaWrapper}>
          <StartButton
            text={ctaText}
            onClick={handleCtaClick}
            className={styles.ctaButton}
          />
        </div>
      </div>
    </section>
  );
};

export default VideoTranslationDemos;
